import { Stack, Text, Box } from '@ltvco/refresh-lib/theme';
import { usePhoneFormatter } from '@ltvco/refresh-lib/utils';
import { IconContainer } from '@ltvco/refresh-lib/v2';

import {
  getFraudStatusForBoolean,
  getFraudStatusColor,
  getFraudStatusIcon,
} from 'components/ChatBV/utils';

import { PhoneSummaryData } from '../DataTypes/SummaryTypes';

import { BoldSpan } from './components';

interface PhoneReportRendererProps {
  data: PhoneSummaryData;
}

export const PhoneReportRenderer = ({ data }: PhoneReportRendererProps) => {
  const formattedPhoneNumber = usePhoneFormatter(data.phone, true);

  // Helper function to safely parse JSON strings or return arrays
  const parseArrayField = (field: string | string[] | undefined): string[] => {
    if (!field) {
      return [];
    }
    if (Array.isArray(field)) {
      return field;
    }
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const associatedNames = parseArrayField(data.associated_names);
  const associatedEmails = parseArrayField(data.associated_emails);

  // Helper function to convert "yes"/"no" strings to boolean
  const yesNoToBoolean = (
    value: string | undefined | null
  ): boolean | undefined => {
    if (value === null || value === undefined) {
      return undefined;
    }
    return value.toLowerCase() === 'yes';
  };

  // Convert to booleans for text display
  const isActive = yesNoToBoolean(data.active_status) ?? false;
  const isRisky = yesNoToBoolean(data.risk) ?? false;
  const isSpammer = yesNoToBoolean(data.is_spammer) ?? false;
  const isDoNotCall = yesNoToBoolean(data.is_do_not_call) ?? false;

  // Get FraudStatus for icons and colors
  const activeStatusFraud = getFraudStatusForBoolean(isActive, true);
  const riskFraud = getFraudStatusForBoolean(isRisky, false);
  const spammerFraud = getFraudStatusForBoolean(isSpammer, false);
  const doNotCallFraud = getFraudStatusForBoolean(isDoNotCall, false);

  const StatusIcon = getFraudStatusIcon(activeStatusFraud);
  const RiskIcon = getFraudStatusIcon(riskFraud);
  const SpammerIcon = getFraudStatusIcon(spammerFraud);
  const DoNotCallIcon = getFraudStatusIcon(doNotCallFraud);

  const StatusColor = getFraudStatusColor(activeStatusFraud);
  const RiskColor = getFraudStatusColor(riskFraud);
  const SpammerColor = getFraudStatusColor(spammerFraud);
  const DoNotCallColor = getFraudStatusColor(doNotCallFraud);

  return (
    <Stack spacing={2}>
      {data.phone && (
        <Text variant="body1">
          Here are the key details for:{' '}
          <BoldSpan>{formattedPhoneNumber}</BoldSpan>{' '}
          {data.location ? `located in ${data.location}` : ''}.
        </Text>
      )}
      <Stack spacing={1.5}>
        <Box>
          <Text variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
            Phone Info:
          </Text>
          <Text variant="body1">
            <BoldSpan>Location:</BoldSpan> {data.location || 'N/A'}
          </Text>
          <Text variant="body1">
            <BoldSpan>Type:</BoldSpan> {data.phone_type || 'N/A'}
          </Text>
          <Text variant="body1">
            <BoldSpan>Provider:</BoldSpan> {data.provider || 'N/A'}
          </Text>

          <Box mb={1} mt={1}>
            <Text variant="body1" mt={1} mb={1}>
              <BoldSpan>Status:</BoldSpan>{' '}
            </Text>
            <Box display="flex" gap={1}>
              <Text variant="body1" sx={{ fontWeight: 700 }}>
                Active Status:
              </Text>
              <IconContainer
                icon={
                  <StatusIcon sx={{ fontSize: 18, color: StatusColor.main }} />
                }
                width={24}
                height={24}
                bgColor={StatusColor.light}
                sx={{
                  flexShrink: 0,
                }}
              />
              {isActive ? 'Active' : 'Disconnected'}
            </Box>
            <Box display="flex" gap={1}>
              <Text variant="body1" sx={{ fontWeight: 700 }}>
                Risk:
              </Text>
              <IconContainer
                icon={<RiskIcon sx={{ fontSize: 18, color: RiskColor.main }} />}
                width={24}
                height={24}
                bgColor={RiskColor.light}
                sx={{
                  flexShrink: 0,
                }}
              />
              {isRisky ? 'Risky' : 'Not Risky'}
            </Box>
            <Box display="flex" gap={1}>
              <Text variant="body1" sx={{ fontWeight: 700 }}>
                Spammer:
              </Text>
              <IconContainer
                icon={
                  <SpammerIcon
                    sx={{ fontSize: 18, color: SpammerColor.main }}
                  />
                }
                width={24}
                height={24}
                bgColor={SpammerColor.light}
                sx={{
                  flexShrink: 0,
                }}
              />
              {isSpammer ? 'Spammer' : 'Not Spammer'}
            </Box>
            <Box display="flex" gap={1}>
              <Text variant="body1" sx={{ fontWeight: 700 }}>
                Do Not Call:
              </Text>
              <IconContainer
                icon={
                  <DoNotCallIcon
                    sx={{ fontSize: 18, color: DoNotCallColor.main }}
                  />
                }
                width={24}
                height={24}
                bgColor={DoNotCallColor.light}
                sx={{
                  flexShrink: 0,
                }}
              />
              {isDoNotCall ? 'On DNC List' : 'Not on DNC List'}
            </Box>
          </Box>

          <Box>
            <Text variant="body1" sx={{ fontWeight: 700 }}>
              Owner:
            </Text>
            <Text variant="body1">
              <BoldSpan>Name:</BoldSpan> {data.owner_name || 'N/A'}
            </Text>
            <Text variant="body1">
              <BoldSpan>Associated Names:</BoldSpan>{' '}
              {associatedNames.length > 0 ? associatedNames.join(', ') : 'N/A'}
            </Text>
            <Text variant="body1">
              <BoldSpan>Associated Emails:</BoldSpan>{' '}
              {associatedEmails.length > 0
                ? associatedEmails.join(', ')
                : 'N/A'}
            </Text>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};
